import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { IoArrowForward, IoCheckmarkCircle, IoSparkles, IoCompassOutline, IoCreateOutline, IoClose, IoAddOutline, IoTrashOutline, IoCalendarOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { getPageConfig, updatePageConfig, createCheckout, createOfferCheckout, getPaymentStatus, setCoachingPrice, getToken } from "@/lib/api";

const DISCORD_INVITE = "https://discord.gg/sFCKWpgRg";

export default function Accompagnement() {
  const { isAdmin, user, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const pollGuard = useRef(false);

  useEffect(() => { getPageConfig("accompagnement").then(setCfg).catch(() => {}); }, []);

  const credits = user?.coaching_credits || 0;

  // Retour depuis le paiement du coaching (?session_id=…)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || pollGuard.current) return;
    pollGuard.current = true;
    let tries = 0;
    const check = async () => {
      try {
        const st = await getPaymentStatus(sid);
        if (st.payment_status === "paid") {
          await refresh();
          toast.success("Paiement confirmé — ton crédit coaching est débloqué !");
          setParams({}, { replace: true });
          return;
        }
      } catch { /* retry */ }
      tries += 1;
      if (tries < 20) setTimeout(check, 2000);
      else setParams({}, { replace: true });
    };
    check();
    // eslint-disable-next-line
  }, []);

  // Bouton "Réserver" : logique selon crédits / connexion
  const handleBook = async () => {
    if (cfg?.renzoku_paid_enabled === false) { window.open(DISCORD_INVITE, "_blank"); return; }
    let u = user;
    if (!u) {
      const tok = getToken();
      if (!tok) { toast.info("Connecte-toi pour réserver ton coaching."); nav("/connexion"); return; }
      u = await refresh();          // hydrate la session en cours
      if (!u) { nav("/connexion"); return; }
    }
    if ((u.coaching_credits || 0) > 0) {
      if (cfg?.renzoku_calendly_url) window.open(cfg.renzoku_calendly_url, "_blank");
      else document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setBooking(true);
    try { const { checkout_url } = await createCheckout("renzoku_coaching"); window.location.href = checkout_url; }
    catch { toast.error("Impossible de démarrer le paiement."); setBooking(false); }
  };

  const book = handleBook;

  // Réservation par formule : prix → Stripe, sinon lien Calendly, sinon coaching principal
  const bookOffer = async (offer) => {
    const priceEur = Number(offer?.price_eur) || 0;
    if (priceEur > 0) {
      let u = user;
      if (!u) {
        const tok = getToken();
        if (!tok) { toast.info("Connecte-toi pour réserver."); nav("/connexion"); return; }
        u = await refresh();
        if (!u) { nav("/connexion"); return; }
      }
      try { const { checkout_url } = await createOfferCheckout(offer.key); window.location.href = checkout_url; }
      catch (e) { toast.error(e?.response?.data?.detail || "Paiement indisponible."); }
      return;
    }
    if (offer?.calendly_url) { window.open(offer.calendly_url, "_blank"); return; }
    handleBook();
  };
  const openEdit = () => { setDraft(JSON.parse(JSON.stringify(cfg))); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      if (Number(draft.renzoku_price_eur) > 0 && Number(draft.renzoku_price_eur) !== Number(cfg.renzoku_price_eur)) {
        await setCoachingPrice(Number(draft.renzoku_price_eur));
      }
      const saved = await updatePageConfig("accompagnement", draft); setCfg(saved); setEditing(false); toast.success("Page Accompagnement mise à jour");
    }
    catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  if (!cfg) return <DojoShell crumbs={[{ label: "Accompagnement" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;

  const included = cfg.included || [];
  const steps = cfg.steps || [];
  const offers = cfg.offers || [];
  const priceLabel = cfg.renzoku_price_eur ? `${cfg.renzoku_price_eur} €` : "50 €";

  return (
    <DojoShell crumbs={[{ label: "Accompagnement" }]}>
      {isAdmin && (
        <button style={ed.editPageBtn} data-testid="accompagnement-edit" onClick={openEdit}><IoCreateOutline size={16} /> Modifier cette page</button>
      )}

      {credits > 0 && (
        <div id="booking" style={s.bookingBanner} data-testid="coaching-booking">
          <div style={{ flex: 1 }}>
            <div style={s.bookingTag}><IoCalendarOutline size={15} /> TON CRÉDIT COACHING EST ACTIF</div>
            <div style={s.bookingTitle}>Tu as {credits} séance{credits > 1 ? "s" : ""} Renzoku à réserver</div>
            <div style={s.bookingSub}>Choisis ton créneau et on se retrouve en tête-à-tête.</div>
          </div>
          {cfg.renzoku_calendly_url ? (
            <a href={cfg.renzoku_calendly_url} target="_blank" rel="noreferrer" style={s.bookingBtn} data-testid="coaching-calendly">Réserver mon créneau <IoArrowForward size={17} /></a>
          ) : (
            <span style={{ ...s.bookingBtn, opacity: 0.7 }}>Le lien de réservation arrive bientôt</span>
          )}
        </div>
      )}

      {/* Hero */}
      <div style={s.hero}>
        <span style={s.heroKanji}>連続</span>
        <div style={s.heroGlow} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 680 }}>
          <div style={s.eyebrow}><IoSparkles size={13} /> {cfg.eyebrow}</div>
          <h1 style={s.h1}>{cfg.title}</h1>
          <p style={s.heroSub} dangerouslySetInnerHTML={{ __html: cfg.intro }} />
          <div style={s.heroCtas}>
            <button style={s.ctaPrimary} data-testid="accompagnement-book" onClick={book}>{credits > 0 ? "Réserver mon créneau" : cfg.renzoku_paid_enabled === false ? "Réserver ma séance" : `Réserver ma séance — ${priceLabel}`} <IoArrowForward size={18} /></button>
            <a href="#formules" style={s.ctaGhost}>Voir les formules</a>
          </div>
        </div>
      </div>

      {/* Ce qui est inclus */}
      <h2 style={s.sectionTitle}>Ce que tu obtiens</h2>
      <div style={s.grid4} data-testid="accompagnement-included">
        {included.map((f, i) => (
          <div key={i} style={s.incCard}>
            <span style={s.incIcon}><IoCompassOutline size={22} /></span>
            <div style={s.incTitle}>{f.title}</div>
            <div style={s.incText}>{f.text}</div>
          </div>
        ))}
      </div>

      {/* Comment ça marche */}
      <h2 style={s.sectionTitle}>Comment ça se passe</h2>
      <div style={s.steps} data-testid="accompagnement-steps">
        {steps.map((st, i) => (
          <div key={i} style={s.step}>
            <span style={s.stepNum}>{st.n}</span>
            <div>
              <div style={s.stepTitle}>{st.t}</div>
              <div style={s.stepDesc}>{st.d}</div>
            </div>
            {i < steps.length - 1 && <span style={s.stepArrow}>→</span>}
          </div>
        ))}
      </div>

      {/* Formules */}
      <h2 id="formules" style={{ ...s.sectionTitle, scrollMarginTop: 90 }}>Choisis ta formule</h2>
      <div style={s.grid3} data-testid="accompagnement-formulas">
        {offers.map((f, i) => (
          <div key={f.key || i} style={{ ...s.formula, ...(f.highlight ? s.formulaHi : {}) }} data-testid={`formula-${f.key || i}`}>
            {f.highlight && <span style={s.popular}>Le plus choisi</span>}
            <div style={{ ...s.fName, color: f.highlight ? "#fff" : "var(--text-primary)" }}>{f.name}</div>
            <div style={{ ...s.fTag, color: f.highlight ? "rgba(255,255,255,0.85)" : "var(--text-secondary)" }}>{f.tagline}</div>
            {f.duration && <div style={{ ...s.fDuration, color: f.highlight ? "rgba(255,255,255,0.92)" : "var(--brand)" }}>⏱ {f.duration}</div>}
            <div style={{ ...s.fPrice, color: f.highlight ? "#fff" : "var(--brand)" }}>{f.price_eur ? `${f.price_eur} €` : f.price}</div>
            <ul style={s.fList}>
              {(f.features || []).map((ft, j) => (
                <li key={j} style={{ ...s.fItem, color: f.highlight ? "rgba(255,255,255,0.92)" : "var(--text-secondary)" }}>
                  <IoCheckmarkCircle size={17} style={{ color: f.highlight ? "#fff" : "var(--brand)", flexShrink: 0, marginTop: 1 }} /> {ft}
                </li>
              ))}
            </ul>
            <button style={{ ...s.fBtn, ...(f.highlight ? s.fBtnHi : {}) }} data-testid={`formula-book-${f.key || i}`} onClick={() => bookOffer(f)}>
              {f.price_eur ? `Réserver — ${f.price_eur} €` : f.calendly_url ? "Prendre rendez-vous" : "Réserver"} <IoArrowForward size={15} />
            </button>
          </div>
        ))}
      </div>
      <p style={s.note}>{cfg.note}</p>

      {/* CTA final */}
      <button style={s.finalCta} data-testid="accompagnement-final-cta" onClick={book}>
        <div style={s.finalGlow} />
        <div style={{ position: "relative", zIndex: 1, flex: 1, textAlign: "left" }}>
          <div style={s.finalEyebrow}>PRÊT·E À AVANCER ?</div>
          <div style={s.finalTitle}>{cfg.final_title}</div>
          <div style={s.finalSub}>{cfg.final_sub}</div>
        </div>
        <span style={s.finalBtn}>Réserver ma séance <IoArrowForward size={18} /></span>
      </button>

      {editing && draft && <EditModal draft={draft} setDraft={setDraft} onClose={() => setEditing(false)} onSave={saveEdit} busy={busy} />}
    </DojoShell>
  );
}

// ---------------- Éditeur admin ----------------
function EditModal({ draft, setDraft, onClose, onSave, busy }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const setList = (k, i, field, v) => { const l = [...(draft[k] || [])]; l[i] = { ...l[i], [field]: v }; set(k, l); };
  const addItem = (k, item) => set(k, [...(draft[k] || []), item]);
  const delItem = (k, i) => set(k, (draft[k] || []).filter((_, x) => x !== i));

  return (
    <div style={ed.overlay} onClick={onClose}>
      <div style={ed.modal} onClick={(e) => e.stopPropagation()} data-testid="accompagnement-edit-modal">
        <div style={ed.head}>
          <span style={ed.title}>Modifier la page Accompagnement</span>
          <button style={ed.x} onClick={onClose} data-testid="accompagnement-edit-close"><IoClose size={20} /></button>
        </div>
        <div style={ed.body}>
          <Lbl>En-tête (petit titre)</Lbl>
          <input style={ed.input} data-testid="acc-edit-eyebrow" value={draft.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} />
          <Lbl>Titre principal</Lbl>
          <input style={ed.input} data-testid="acc-edit-title" value={draft.title || ""} onChange={(e) => set("title", e.target.value)} />
          <Lbl>Introduction (HTML simple autorisé : &lt;strong&gt;)</Lbl>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={3} data-testid="acc-edit-intro" value={draft.intro || ""} onChange={(e) => set("intro", e.target.value)} />

          <Divider label="Coaching payant (Renzoku)" />
          <label style={ed.check}><input type="checkbox" checked={draft.renzoku_paid_enabled !== false} onChange={(e) => set("renzoku_paid_enabled", e.target.checked)} style={{ accentColor: "var(--brand)" }} data-testid="acc-edit-paid-toggle" /> Activer le paiement en ligne (sinon, réservation via Discord)</label>
          <Lbl>Prix de la séance (€) — met à jour Stripe à l'enregistrement</Lbl>
          <input style={ed.input} type="number" min="1" step="1" data-testid="acc-edit-price" value={draft.renzoku_price_eur ?? 50} onChange={(e) => set("renzoku_price_eur", e.target.value)} />
          <Lbl>Lien Calendly (réservation après paiement)</Lbl>
          <input style={ed.input} data-testid="acc-edit-calendly" placeholder="https://calendly.com/ton-lien" value={draft.renzoku_calendly_url || ""} onChange={(e) => set("renzoku_calendly_url", e.target.value)} />

          <Divider label="Ce que tu obtiens" onAdd={() => addItem("included", { title: "Nouveau", text: "" })} />
          {(draft.included || []).map((it, i) => (
            <Row key={i} onDel={() => delItem("included", i)}>
              <input style={ed.input} placeholder="Titre" value={it.title || ""} onChange={(e) => setList("included", i, "title", e.target.value)} />
              <input style={ed.input} placeholder="Texte" value={it.text || ""} onChange={(e) => setList("included", i, "text", e.target.value)} />
            </Row>
          ))}

          <Divider label="Étapes" onAdd={() => addItem("steps", { n: "0", t: "Nouvelle étape", d: "" })} />
          {(draft.steps || []).map((it, i) => (
            <Row key={i} onDel={() => delItem("steps", i)}>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...ed.input, width: 60 }} placeholder="N°" value={it.n || ""} onChange={(e) => setList("steps", i, "n", e.target.value)} />
                <input style={{ ...ed.input, flex: 1 }} placeholder="Titre" value={it.t || ""} onChange={(e) => setList("steps", i, "t", e.target.value)} />
              </div>
              <input style={ed.input} placeholder="Description" value={it.d || ""} onChange={(e) => setList("steps", i, "d", e.target.value)} />
            </Row>
          ))}

          <Divider label="Formules" onAdd={() => addItem("offers", { key: "offre_" + Date.now(), name: "Nouvelle formule", tagline: "", price: "Sur demande", duration: "", features: [], highlight: false })} />
          {(draft.offers || []).map((it, i) => (
            <Row key={i} onDel={() => delItem("offers", i)}>
              <input style={ed.input} placeholder="Nom" value={it.name || ""} onChange={(e) => setList("offers", i, "name", e.target.value)} />
              <input style={ed.input} placeholder="Sous-titre" value={it.tagline || ""} onChange={(e) => setList("offers", i, "tagline", e.target.value)} />
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...ed.input, flex: 1 }} placeholder="Durée (ex: 1h · 1:1)" value={it.duration || ""} onChange={(e) => setList("offers", i, "duration", e.target.value)} />
                <input style={{ ...ed.input, flex: 1 }} placeholder="Prix affiché (texte)" value={it.price || ""} onChange={(e) => setList("offers", i, "price", e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...ed.input, flex: 1 }} type="number" min="0" step="1" placeholder="Prix payant (€) — Stripe" value={it.price_eur ?? ""} data-testid={`offer-price-${i}`} onChange={(e) => setList("offers", i, "price_eur", e.target.value ? Number(e.target.value) : "")} />
                <input style={{ ...ed.input, flex: 2 }} placeholder="Lien Calendly (si gratuit / prise de RDV)" value={it.calendly_url || ""} data-testid={`offer-calendly-${i}`} onChange={(e) => setList("offers", i, "calendly_url", e.target.value)} />
              </div>
              <p style={{ fontSize: 11.5, color: "var(--text-secondary)", margin: "2px 0 0" }}>💡 Mets un prix (€) pour un paiement Stripe, OU un lien Calendly pour une prise de rendez-vous directe.</p>
              <textarea style={{ ...ed.input, resize: "vertical" }} rows={3} placeholder="Un avantage par ligne" value={(it.features || []).join("\n")} onChange={(e) => setList("offers", i, "features", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
              <label style={ed.check}><input type="checkbox" checked={!!it.highlight} onChange={(e) => setList("offers", i, "highlight", e.target.checked)} style={{ accentColor: "var(--brand)" }} /> Mettre en avant</label>
            </Row>
          ))}

          <Divider label="Bas de page" />
          <Lbl>Note sous les formules</Lbl>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={2} value={draft.note || ""} onChange={(e) => set("note", e.target.value)} />
          <Lbl>Titre du bloc final</Lbl>
          <input style={ed.input} value={draft.final_title || ""} onChange={(e) => set("final_title", e.target.value)} />
          <Lbl>Sous-titre du bloc final</Lbl>
          <input style={ed.input} value={draft.final_sub || ""} onChange={(e) => set("final_sub", e.target.value)} />
        </div>
        <div style={ed.actions}>
          <button style={ed.cancel} onClick={onClose}>Annuler</button>
          <button style={{ ...ed.save, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="accompagnement-save" onClick={onSave}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

const Lbl = ({ children }) => <label style={ed.lbl}>{children}</label>;
const Divider = ({ label, onAdd }) => (
  <div style={ed.dividerRow}>
    <span style={ed.dividerLabel}>{label}</span>
    {onAdd && <button style={ed.addBtn} onClick={onAdd}><IoAddOutline size={15} /> Ajouter</button>}
  </div>
);
const Row = ({ children, onDel }) => (
  <div style={ed.itemRow}>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    <button style={ed.delBtn} onClick={onDel}><IoTrashOutline size={16} /></button>
  </div>
);

const s = {
  bookingBanner: { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", padding: "22px 24px", borderRadius: 20, marginBottom: 22, background: `linear-gradient(135deg, ${RED}, color-mix(in srgb, var(--brand) 55%, #000))`, color: "#fff", boxShadow: `0 18px 44px color-mix(in srgb, var(--brand) 34%, transparent)` },
  bookingTag: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 800, letterSpacing: 1, opacity: 0.9 },
  bookingTitle: { fontSize: 20, fontWeight: 900, margin: "6px 0 4px", fontFamily: "var(--font-display)" },
  bookingSub: { fontSize: 14, opacity: 0.92 },
  bookingBtn: { display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", color: RED, fontWeight: 900, fontSize: 14.5, padding: "13px 22px", borderRadius: 999, textDecoration: "none", whiteSpace: "nowrap" },
  hero: { position: "relative", overflow: "hidden", marginBottom: 34, padding: "40px 30px", borderRadius: 26, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  heroKanji: { position: "absolute", right: -10, top: -30, fontSize: 190, fontWeight: 800, color: "var(--text-primary)", opacity: 0.05, lineHeight: 1, fontFamily: "var(--font-display)", pointerEvents: "none" },
  heroGlow: { position: "absolute", bottom: -90, left: -50, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent), transparent 70%)" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 6, color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 2 },
  h1: { fontSize: 40, fontWeight: 900, margin: "10px 0 12px", color: "var(--text-primary)", fontFamily: "var(--font-display)", lineHeight: 1.05 },
  heroSub: { color: "var(--text-secondary)", fontSize: 16, lineHeight: 1.65, margin: "0 0 22px" },
  heroCtas: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  ctaPrimary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 24px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", boxShadow: `0 14px 34px color-mix(in srgb, var(--brand) 35%, transparent)` },
  ctaGhost: { display: "inline-flex", alignItems: "center", padding: "14px 22px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "transparent", color: "var(--text-primary)", fontWeight: 700, fontSize: 14, cursor: "pointer", textDecoration: "none" },
  sectionTitle: { fontSize: 14, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text-secondary)", margin: "0 0 16px" },
  grid4: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16, marginBottom: 38 },
  incCard: { padding: "22px 20px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  incIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: 14, background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: RED, marginBottom: 14 },
  incTitle: { fontSize: 16.5, fontWeight: 800, color: "var(--text-primary)", marginBottom: 6 },
  incText: { fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)" },
  steps: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 40 },
  step: { position: "relative", display: "flex", gap: 14, padding: "22px 20px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  stepNum: { fontSize: 30, fontWeight: 900, color: RED, fontFamily: "var(--font-display)", lineHeight: 1, flexShrink: 0, opacity: 0.9 },
  stepTitle: { fontSize: 16, fontWeight: 800, color: "var(--text-primary)", marginBottom: 5 },
  stepDesc: { fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)" },
  stepArrow: { position: "absolute", right: -13, top: "50%", transform: "translateY(-50%)", fontSize: 22, color: "var(--border-c)", zIndex: 2, fontWeight: 800 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18, marginBottom: 12 },
  formula: { position: "relative", display: "flex", flexDirection: "column", padding: "28px 24px", borderRadius: 22, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  formulaHi: { border: "none", background: `linear-gradient(150deg, ${RED}, color-mix(in srgb, var(--brand) 55%, #000))`, boxShadow: `0 22px 55px color-mix(in srgb, var(--brand) 32%, transparent)`, transform: "scale(1.015)" },
  popular: { position: "absolute", top: 16, right: 16, fontSize: 10.5, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase", color: RED, background: "#fff", padding: "4px 10px", borderRadius: 999 },
  fName: { fontSize: 20, fontWeight: 900, fontFamily: "var(--font-display)" },
  fTag: { fontSize: 13, marginTop: 4 },
  fDuration: { fontSize: 12.5, fontWeight: 800, marginTop: 10 },
  fPrice: { fontSize: 24, fontWeight: 900, margin: "8px 0 18px" },
  fList: { listStyle: "none", padding: 0, margin: "0 0 22px", display: "flex", flexDirection: "column", gap: 11, flex: 1 },
  fItem: { display: "flex", gap: 9, fontSize: 13.5, lineHeight: 1.4 },
  fBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 20px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  fBtnHi: { background: "#fff", color: RED },
  note: { fontSize: 13, color: "var(--text-secondary)", textAlign: "center", margin: "0 0 34px", lineHeight: 1.5 },
  finalCta: { position: "relative", overflow: "hidden", width: "100%", display: "flex", alignItems: "center", gap: 20, padding: "32px 28px", borderRadius: 24, border: "1px solid var(--border-c)", cursor: "pointer", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", flexWrap: "wrap" },
  finalGlow: { position: "absolute", top: -80, right: -40, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 24%, transparent), transparent 70%)" },
  finalEyebrow: { color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 2 },
  finalTitle: { color: "var(--text-primary)", fontSize: 25, fontWeight: 900, margin: "6px 0 6px", fontFamily: "var(--font-display)" },
  finalSub: { color: "var(--text-secondary)", fontSize: 14.5 },
  finalBtn: { position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 8, background: RED, color: "#fff", fontWeight: 900, fontSize: 15, padding: "14px 24px", borderRadius: 999, whiteSpace: "nowrap" },
};

const ed = {
  editPageBtn: { display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16, padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  overlay: { position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "36px 16px", overflowY: "auto" },
  modal: { width: "100%", maxWidth: 640, background: "var(--bg-secondary)", borderRadius: 18, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border-c)" },
  title: { fontSize: 16.5, fontWeight: 900, color: "var(--text-primary)" },
  x: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer", color: "var(--text-secondary)" },
  body: { padding: 20, display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflowY: "auto" },
  lbl: { fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)", marginTop: 6 },
  input: { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" },
  dividerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 6px", paddingTop: 14, borderTop: "1px solid var(--border-c)" },
  dividerLabel: { fontSize: 13, fontWeight: 900, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 1 },
  addBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  itemRow: { display: "flex", gap: 8, padding: 12, borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", marginBottom: 8 },
  delBtn: { width: 34, height: 34, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: RED, cursor: "pointer" },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "var(--text-primary)", cursor: "pointer" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid var(--border-c)" },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer" },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer" },
};
